import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { Mail, MessageSquare, Twitter, MapPin, CheckCircle2, X } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";
import { useState, useEffect } from "react";

const SITE_URL = "https://letslistbusiness.com";
const ACCESS_KEY = "215968af-7166-4379-9985-ab56fbdafa38";
const RECIPIENT  = "emerharrison4@gmail.com";
const SUBJECT    = "[letslistbusiness.com] New Contact Form";

export const Route = createFileRoute("/contact/")(({
  head: () => {
    const title = "Contact LetsListBusiness — Support, Feedback, Press";
    const description =
      "Get in touch with the LetsListBusiness team for support, feedback, partnerships and press inquiries.";
    const url = SITE_URL + "/contact/";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "Contact LetsListBusiness — Support, Feedback, Press" },
        {
          property: "og:description",
          content: "Get in touch with the team for support, feedback, partnerships and press inquiries.",
        },
        { property: "og:image", content: "https://images.unsplash.com/photo-1534536281715-e28d76689b4d?w=1200&q=80" },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "Contact LetsListBusiness" },
        { name: "twitter:description", content: "Reach out to us for any questions or support." },
        { name: "twitter:image", content: "https://images.unsplash.com/photo-1534536281715-e28d76689b4d?w=1200&q=80" },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema(
          [
            { label: "Home", href: "/" },
            { label: "Contact", href: "/contact/" },
          ],
          SITE_URL
        ),
      ],
    };
  },
  component: ContactPage,
} as any));

/* ─── Toast ─────────────────────────────────────────────────────────────── */
function Toast({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (visible) {
      const t = setTimeout(onClose, 5000);
      return () => clearTimeout(t);
    }
  }, [visible, onClose]);

  return (
    <>
      <div
        style={{
          position: "fixed",
          bottom: "1.5rem",
          right: "1.5rem",
          zIndex: 9999,
          transform: visible ? "translateY(0)" : "translateY(120%)",
          opacity: visible ? 1 : 0,
          transition: "transform 0.4s cubic-bezier(.22,1,.36,1), opacity 0.3s ease",
          pointerEvents: visible ? "auto" : "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.75rem",
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            border: "1px solid rgba(99,102,241,0.4)",
            borderRadius: "1rem",
            padding: "1rem 1.25rem",
            boxShadow:
              "0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(99,102,241,0.15)",
            minWidth: "300px",
            maxWidth: "360px",
          }}
        >
          <div
            style={{
              flexShrink: 0,
              width: "2.25rem",
              height: "2.25rem",
              borderRadius: "50%",
              background: "rgba(34,197,94,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircle2 size={18} color="#22c55e" />
          </div>
          <div style={{ flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontWeight: 700,
                fontSize: "0.875rem",
                color: "#f1f5f9",
              }}
            >
              Message sent!
            </p>
            <p
              style={{
                margin: "0.2rem 0 0",
                fontSize: "0.75rem",
                color: "#94a3b8",
                lineHeight: 1.5,
              }}
            >
              We'll get back to you within one business day.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#64748b",
              padding: "0.1rem",
              lineHeight: 0,
              flexShrink: 0,
            }}
            aria-label="Close notification"
          >
            <X size={15} />
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────── */
function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    inquiry: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [submitError, setSubmitError] = useState("");


  const inquiryOptions = [
    { value: "", label: "Select an inquiry type" },
    { value: "general", label: "General Enquiry" },
    { value: "listing", label: "Listing Support" },
    { value: "partnership", label: "Partnership / Collaboration" },
    { value: "press", label: "Press & Media" },
    { value: "technical", label: "Technical Issue" },
    { value: "billing", label: "Billing & Account" },
    { value: "other", label: "Other" },
  ];

  function validate() {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.email.trim()) {
      e.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Please enter a valid email.";
    }
    if (!form.inquiry) e.inquiry = "Please select an inquiry type.";
    if (!form.message.trim()) e.message = "Message is required.";
    else if (form.message.trim().length < 10)
      e.message = "Message must be at least 10 characters.";
    return e;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitError("");
    setSubmitting(true);

    const formData = new FormData(e.currentTarget);
    formData.set("access_key", ACCESS_KEY);
    formData.set("to", RECIPIENT);
    formData.set("subject", SUBJECT);
    // include inquiry label in the body sent to Web3Forms
    formData.set(
      "message",
      `Inquiry type: ${form.inquiry}\n\n${form.message}`
    );

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        setForm({ name: "", email: "", inquiry: "", message: "" });
        setToastVisible(true);
      } else {
        setSubmitError(data.message || "Something went wrong. Please try again.");
      }
    } catch (_) {
      setSubmitError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Toast visible={toastVisible} onClose={() => setToastVisible(false)} />
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Contact" }]} />

      <section className="container mx-auto px-4 pb-16">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-card-lg">
          <div className="grid lg:grid-cols-5">
            {/* Left — dark info panel */}
            <div className="bg-secondary p-8 text-secondary-foreground sm:p-12 lg:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-accent">
                Get in touch
              </div>
              <h1 className="mt-4 font-display text-4xl font-bold leading-tight sm:text-5xl">
                We read every{" "}
                <em className="not-italic text-accent">message.</em>
              </h1>
              <p className="mt-5 text-secondary-foreground/80">
                For listing issues, press inquiries, or just to say hi — drop
                us a line. We typically respond within one business day.
              </p>

              <div className="mt-10 space-y-5">
                <Channel icon={Mail} label="General" value="hello@letslistbusiness.com" />
                <Channel icon={MessageSquare} label="Press" value="press@letslistbusiness.com" />
                <Channel icon={Twitter} label="Social" value="@localix" />
                <Channel icon={MapPin} label="HQ" value="Brooklyn, NY" />
              </div>
            </div>

            {/* Right — form */}
            <form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-5 p-8 sm:p-12 lg:col-span-3"
            >
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                  Send us a note
                </div>
                <h2 className="mt-2 font-display text-2xl font-bold">
                  How can we help?
                </h2>
              </div>

              {/* Name */}
              <Field label="Name" error={errors.name}>
                <input
                  id="contact-name"
                  name="name"
                  className={`field${errors.name ? " field-error" : ""}`}
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoComplete="name"
                />
              </Field>

              {/* Email */}
              <Field label="Email" error={errors.email}>
                <input
                  id="contact-email"
                  name="email"
                  className={`field${errors.email ? " field-error" : ""}`}
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  autoComplete="email"
                />
              </Field>

              {/* Inquiry Type */}
              <Field label="Inquiry Type" error={errors.inquiry}>
                <select
                  id="contact-inquiry"
                  name="inquiry"
                  className={`field${errors.inquiry ? " field-error" : ""}${!form.inquiry ? " field-placeholder" : ""}`}
                  value={form.inquiry}
                  onChange={(e) => setForm({ ...form, inquiry: e.target.value })}
                >
                  {inquiryOptions.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.value === ""}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Message */}
              <Field label="Message" error={errors.message}>
                <textarea
                  id="contact-message"
                  name="message"
                  className={`field min-h-[150px]${errors.message ? " field-error" : ""}`}
                  placeholder="Tell us more about how we can help…"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </Field>

              {submitError && (
                <div className="rounded-lg bg-destructive/10 p-3 text-sm font-medium text-destructive">
                  {submitError}
                </div>
              )}

              <button
                id="contact-submit"
                type="submit"
                disabled={submitting}
                className="rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60 transition-opacity"
              >
                {submitting ? "Sending…" : "Send message"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <style>{`
        .field {
          width: 100%;
          border-radius: .625rem;
          border: 1px solid var(--border);
          background: var(--background);
          padding: .7rem .9rem;
          font-size: .875rem;
          outline: none;
          transition: all .15s;
          appearance: none;
          -webkit-appearance: none;
          color: var(--foreground);
        }
        .field:focus {
          border-color: var(--ring);
          box-shadow: 0 0 0 3px color-mix(in oklab, var(--ring) 25%, transparent);
        }
        .field-error {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.15) !important;
        }
        .field-placeholder {
          color: var(--muted-foreground);
        }
        select.field {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          padding-right: 2.5rem;
          cursor: pointer;
        }
        select.field:hover {
          border-color: var(--ring);
        }
        textarea.field {
          resize: vertical;
        }
        .err-msg {
          margin-top: 0.3rem;
          font-size: 0.72rem;
          color: #ef4444;
          font-weight: 500;
        }
      `}</style>
    </>
  );
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */
function Channel({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-4 border-b border-secondary-foreground/15 pb-4">
      <Icon className="h-5 w-5 text-accent" />
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-secondary-foreground/60">
          {label}
        </div>
        <div className="mt-0.5 text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
        {label}
      </span>
      {children}
      {error && <p className="err-msg">{error}</p>}
    </label>
  );
}
